// src/utils/likeManager.ts
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * いいねイベントの詳細情報
 */
export interface LikeEventDetail {
  modelId: string;  // 対象モデルのID
  liked: boolean;   // 新しいいいね状態
  count?: number;   // 新しいいいね数（オプション）
}

// カスタムイベント名
export const LIKE_EVENT_NAME = 'orifoli-model-like-update';

/**
 * いいね状態の変更イベントを発火する関数
 */
export function dispatchLikeEvent(detail: LikeEventDetail): void {
  const event = new CustomEvent<LikeEventDetail>(LIKE_EVENT_NAME, {
    detail,
    bubbles: true,
    cancelable: true
  });
  document.dispatchEvent(event);
}

/**
 * いいね状態の変更を監視するためのリスナーを登録
 */
export function listenToLikeEvent(
  modelId: string,
  callback: (detail: LikeEventDetail) => void
): () => void {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<LikeEventDetail>;
    if (customEvent.detail.modelId === modelId) {
      callback(customEvent.detail);
    }
  };

  document.addEventListener(LIKE_EVENT_NAME, handler);
  return () => document.removeEventListener(LIKE_EVENT_NAME, handler);
}

// バッチリクエストの間隔（ミリ秒）
const BATCH_INTERVAL = 300;

/**
 * いいね状態の管理を一元化するクラス
 */
class LikeManager {
  private cache: Map<string, boolean> = new Map();
  private pendingRequests: Set<string> = new Set();
  private batchTimeout: NodeJS.Timeout | null = null;
  private currentUser: string | null = null;
  private callbacks: Map<string, Set<(liked: boolean) => void>> = new Map();
  private fetchPromises: Map<string, Promise<boolean>> = new Map();

  /**
   * 現在のユーザーを設定
   */
  setUser(userId: string | null) {
    if (this.currentUser !== userId) {
      this.currentUser = userId;
      this.clearCache();
    }
  }

  /**
   * いいね状態を非同期で取得
   */
  async isLiked(modelId: string): Promise<boolean> {
    if (!this.currentUser) return false;

    const cacheKey = `${this.currentUser}:${modelId}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    if (this.fetchPromises.has(cacheKey)) {
      return this.fetchPromises.get(cacheKey)!;
    }

    const promise = new Promise<boolean>((resolve) => {
      if (!this.callbacks.has(cacheKey)) {
        this.callbacks.set(cacheKey, new Set());
      }
      this.callbacks.get(cacheKey)!.add(resolve);

      this.pendingRequests.add(modelId);
      this.scheduleBatchFetch();
    });

    this.fetchPromises.set(cacheKey, promise);
    return promise;
  }

  /**
   * いいね状態を切り替える
   */
  async toggleLike(modelId: string): Promise<boolean> {
    if (!this.currentUser) return false;

    try {
      const cacheKey = `${this.currentUser}:${modelId}`;
      const currentState = await this.isLiked(modelId);
      const newState = !currentState;

      // 楽観的UI更新
      this.cache.set(cacheKey, newState);

      // イベントを発火
      dispatchLikeEvent({
        modelId,
        liked: newState,
      });

      // サーバーに状態を送信
      const functions = getFunctions(undefined, 'asia-northeast1');
      const toggleLike = httpsCallable(functions, 'toggleLike');
      const result = await toggleLike({ modelId });
      const data = result.data as { success: boolean; liked: boolean; count?: number };

      if (data.success) {
        this.cache.set(cacheKey, data.liked);

        // サーバーからの結果が期待と異なる場合は再通知
        if (data.liked !== newState || data.count !== undefined) {
          dispatchLikeEvent({
            modelId,
            liked: data.liked,
            count: data.count
          });
        }

        return data.liked;
      } else {
        // エラー時は元の状態に戻す
        this.cache.set(cacheKey, currentState);
        dispatchLikeEvent({ modelId, liked: currentState });
        return currentState;
      }
    } catch (error) {
      console.error('いいね切り替えエラー:', error);
      this.pendingRequests.add(modelId);
      this.scheduleBatchFetch();
      return false;
    }
  }

  /**
   * バッチリクエスト
   */
  private async batchFetch(modelIds: string[]): Promise<void> {
    if (!this.currentUser || modelIds.length === 0) return;

    try {
      const functions = getFunctions(undefined, 'asia-northeast1');
      const checkBatchLikes = httpsCallable(functions, 'checkBatchLikes');

      const result = await checkBatchLikes({ modelIds: modelIds });
      const data = result.data as {
        success: boolean;
        likes: Array<{ modelId: string; liked: boolean; count?: number }>
      };

      if (data.success) {
        data.likes.forEach(item => {
          const cacheKey = `${this.currentUser}:${item.modelId}`;
          this.cache.set(cacheKey, item.liked);
          this.fetchPromises.delete(cacheKey);

          if (this.callbacks.has(cacheKey)) {
            this.callbacks.get(cacheKey)!.forEach(callback => {
              callback(item.liked);
            });
            this.callbacks.delete(cacheKey);
          }
        });
      }
    } catch (error) {
      console.error('いいね状態の一括取得エラー:', error);

      modelIds.forEach(id => {
        const cacheKey = `${this.currentUser}:${id}`;
        if (this.callbacks.has(cacheKey)) {
          this.callbacks.get(cacheKey)!.forEach(callback => {
            callback(false);
          });
          this.callbacks.delete(cacheKey);
          this.fetchPromises.delete(cacheKey);
        }
      });
    } finally {
      modelIds.forEach(id => {
        this.pendingRequests.delete(id);
      });
    }
  }

  /**
   * バッチリクエストをスケジュール
   */
  private scheduleBatchFetch() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      const modelIds = Array.from(this.pendingRequests);
      if (modelIds.length > 0) {
        this.batchFetch(modelIds);
      }
      this.batchTimeout = null;
    }, BATCH_INTERVAL);
  }

  /**
   * キャッシュをクリア
   */
  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
    this.callbacks.clear();
    this.fetchPromises.clear();

    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
  }
}

// シングルトンインスタンス
export const likeManager = new LikeManager();
